import { useMemo, useSyncExternalStore } from 'react';

import type { Opportunity } from '../../shared/opportunity';

/**
 * The event card under the pointer, from 900px up, for the day focus to
 * preview: where it would go in the day, and what it would do to the route.
 *
 * Settled rather than live: a card counts once the pointer has rested on it
 * for a beat, so running the pointer (or a scroll) past ten cards does not
 * redraw the sidebar ten times. Moving from one card to the next inside that
 * beat never passes through "no card", so the sidebar does not flash back.
 *
 * A store rather than state in App, so that only the sidebar redraws on a
 * hover, not the page and its list.
 */

const SETTLE_MS = 120;

export interface HoverStore {
  /** Called by the list on enter (the event) and leave (null). */
  set: (event: Opportunity | null) => void;
  subscribe: (onChange: () => void) => () => void;
  get: () => Opportunity | null;
}

export function useHoverStore(): HoverStore {
  return useMemo(() => {
    let current: Opportunity | null = null;
    let timer: number | undefined;
    const listeners = new Set<() => void>();
    return {
      set: (event) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          if (event?.id === current?.id) return;
          current = event;
          listeners.forEach((l) => l());
        }, SETTLE_MS);
      },
      subscribe: (onChange) => {
        listeners.add(onChange);
        return () => listeners.delete(onChange);
      },
      get: () => current,
    };
  }, []);
}

/** The settled hovered event, for a component that previews it. */
export function useHovered(store: HoverStore): Opportunity | null {
  return useSyncExternalStore(store.subscribe, store.get);
}
